import { useCallback, useMemo } from 'react';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkSplitSelectedListingsDialog } from 'src/dialogs/BulkEdits/BulkSplitSelectedListingsDialog';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  Event,
  ListingQuery,
  ValueTupleOfBulkEditPreviewAndIDictionaryOfLongAndListOfInteger,
} from 'src/WebApiController';

import { INVENTORY_BULK_SPLIT_SELECTED_LISTINGS_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchSplitSelectedListings } from './LaunchSplitSelectedListings';

export const useBulkSplitSelectedListingsAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `${INVENTORY_BULK_SPLIT_SELECTED_LISTINGS_KEY}-event-${event.virtId}`
    : INVENTORY_BULK_SPLIT_SELECTED_LISTINGS_KEY;

  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const { openDialog, closeDialog } = useDialog(
    DialogId.BulkSplitSelectedListings,
    BulkSplitSelectedListingsDialog
  );

  const onSubmit = useCallback(
    async (
      input?: { [key: string]: number[] },
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails,
      retainSeatTraits?: boolean
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkSplitSelectedListingsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkSplitSelectedListingsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const progress = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkSplitSelectedListings(
              {
                item1: preview!.preview,
                item2: input,
              } as ValueTupleOfBulkEditPreviewAndIDictionaryOfLongAndListOfInteger,
              updateKey,
              supportBackgroundProcess,
              retainSeatTraits ?? true
            );

            if (!supportBackgroundProcess) {
              if (progress) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest data can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkSplitSelectedListings', error, {
              trackErrorData: { input },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const onLaunchAction = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }

      openDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeDialog,
        event,
        filterQueryWithEventIds,
      });
    },
    [
      setIsLoading,
      filterQueryWithEventIds,
      event,
      setListItemExpansion,
      openDialog,
      updateKey,
      onSubmit,
      closeDialog,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.SplitSelected,
        onClick: onLaunchAction,
        disabled: isLoading,
      },
    ];
  }, [isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchSplitSelectedListings
        key="SplitSelectedListings"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
